import React from 'react';
import { RaceMeeting, Race } from '../types';
import { isNonRunner } from '../utils/helpers';
import { Trophy, BarChart3, Target, Check, X, Clock, Ban, Hash } from 'lucide-react';
import './SummaryStats.css';

interface SummaryStatsProps {
  meetings: RaceMeeting[];
  races: Race[];
}

const SummaryStats: React.FC<SummaryStatsProps> = ({ races }) => {
  // Calculate statistics
  const totalRaces = races.length;
  
  const wonRaces = races.filter(race => {
    if (!race.race_result) return false;
    // Check if it's a non-runner first
    if (isNonRunner(race.ai_selection, race.non_runner)) return false;
    
    const normalizeName = (name: string) => name.replace(/\s*\([^)]*\)\s*$/, '').trim();
    return normalizeName(race.race_result) === normalizeName(race.ai_selection);
  });
  
  const lostRaces = races.filter(race => {
    if (!race.race_result) return false;
    // Check if it's a non-runner first - non-runners are not considered losses
    if (isNonRunner(race.ai_selection, race.non_runner)) return false;
    
    const normalizeName = (name: string) => name.replace(/\s*\([^)]*\)\s*$/, '').trim();
    return normalizeName(race.race_result) !== normalizeName(race.ai_selection);
  });
  
  const voidRaces = races.filter(race => {
    if (!race.race_result) return false;
    return isNonRunner(race.ai_selection, race.non_runner);
  });
  
  const pendingRaces = races.filter(race => !race.race_result);
  
  // Calculate total stake
  const totalStake = races.reduce((sum, race) => {
    const stake = parseFloat(race.suggested_stake || '0');
    return sum + stake;
  }, 0);
  
  // Calculate average odds
  const validOdds = races.filter(race => race.bet365_decimal_odds && race.bet365_decimal_odds !== 'N/A');
  const averageOdds = validOdds.length > 0 
    ? validOdds.reduce((sum, race) => sum + parseFloat(race.bet365_decimal_odds || '0'), 0) / validOdds.length
    : 0;
  
  // Calculate Win Rate (excluding void races)
  const completedRaces = wonRaces.length + lostRaces.length;
  const winRate = completedRaces > 0 ? (wonRaces.length / completedRaces) * 100 : 0;

  return (
    <div className="summary-stats">
      {/* Desktop: one row — Qualifying Races, Total Stake, Avg Odds, Win Rate, Won, Lost, Pending, Void */}
      <div className="stat-tile">
        <div className="stat-icon">
          <Trophy size={20} />
        </div>
        <div className="stat-content">
          <span className="stat-value">{totalRaces}</span>
          <span className="stat-label">Qualifying Races</span>
        </div>
      </div>
      
      <div className="stat-tile">
        <div className="stat-icon">
          <Hash size={20} />
        </div>
        <div className="stat-content">
          <span className="stat-value">{totalStake.toFixed(2)}</span>
          <span className="stat-label">Total Stake (pts)</span>
        </div>
      </div>
      
      <div className="stat-tile">
        <div className="stat-icon">
          <BarChart3 size={20} />
        </div>
        <div className="stat-content">
          <span className="stat-value">{averageOdds.toFixed(2)}</span>
          <span className="stat-label">Average Odds</span>
        </div>
      </div>
      
      <div className={`stat-tile ${winRate >= 50 ? 'won' : winRate >= 30 ? 'pending' : 'lost'}`}>
        <div className="stat-icon">
          <Target size={20} />
        </div>
        <div className="stat-content">
          <span className="stat-value">{winRate.toFixed(1)}%</span>
          <span className="stat-label">Win Rate</span>
        </div>
      </div>
      
      <div className="stat-tile won">
        <div className="stat-icon">
          <Check size={20} />
        </div>
        <div className="stat-content">
          <span className="stat-value">{wonRaces.length}</span>
          <span className="stat-label">Won</span>
        </div>
      </div>
      
      <div className="stat-tile lost">
        <div className="stat-icon">
          <X size={20} />
        </div>
        <div className="stat-content">
          <span className="stat-value">{lostRaces.length}</span>
          <span className="stat-label">Lost</span>
        </div>
      </div>
      
      <div className="stat-tile pending">
        <div className="stat-icon">
          <Clock size={20} />
        </div>
        <div className="stat-content">
          <span className="stat-value">{pendingRaces.length}</span>
          <span className="stat-label">Pending</span>
        </div>
      </div>
      
      <div className="stat-tile void">
        <div className="stat-icon">
          <Ban size={20} />
        </div>
        <div className="stat-content">
          <span className="stat-value">{voidRaces.length}</span>
          <span className="stat-label">Void</span>
        </div>
      </div>
    </div>
  );
};

export default SummaryStats;
